import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
} from '@mui/material';
import { adminAPI } from '../../services/api';
import UserTable from '../../components/UserTable';
import UserActionsModal from '../../components/UserActionsModal';
import PortalLayout from '../../components/PortalLayout';
import { useToast } from '../../context/ToastContext';

const GESELLSCHAFTSFORMEN = ['GmbH', 'AG', 'UG (haftungsbeschränkt)', 'GmbH & Co. KG', 'KG', 'OHG', 'GbR', 'Einzelunternehmen (e.K.)', 'e.V.', 'Sonstige'];
const ANREDE_OPTIONS = ['Herr', 'Frau', 'Herr Dr.', 'Frau Dr.', 'Herr Prof.', 'Frau Prof.'];

const AdminUserManagementPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState<'role' | 'deactivate'>('role');

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [filters, setFilters] = useState({ search: '', role: '', status: '' });
  const [pagination, setPagination] = useState({ page: 1, total: 0, perPage: 20 });

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.page,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
      };
      const response = await adminAPI.getUsers(params);
      setUsers(response.data.data);
      setPagination((prev) => ({ ...prev, total: response.data.total, perPage: response.data.per_page }));
    } catch {
      showError('Fehler beim Laden der Nutzer');
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page, showError]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleActionClick = (user: any, action: 'role' | 'deactivate') => {
    setSelectedUser(user);
    setModalAction(action);
    setOpenModal(true);
  };

  const handleEditClick = (user: any) => {
    setEditUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      company_name: user.company_name || '',
      anrede: user.anrede || '',
      gesellschaftsform: user.gesellschaftsform || '',
      position: user.position || '',
      strasse: user.strasse || '',
      plz: user.plz || '',
      ort: user.ort || '',
      land: user.land || 'Deutschland',
      fax: user.fax || '',
      mobil: user.mobil || '',
      homepage: user.homepage || '',
      ust_id: user.ust_id || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setIsSaving(true);
    try {
      await adminAPI.updateUser(editUser.id, editForm);
      showSuccess('Profil gespeichert');
      setEditOpen(false);
      loadUsers();
    } catch {
      showError('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.perPage);

  if (isLoading && users.length === 0) {
    return (
      <PortalLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 4 }}>
          Nutzerverwaltung
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Suche"
              placeholder="Name, E-Mail oder Unternehmen..."
              variant="outlined"
              size="small"
              value={filters.search}
              onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPagination((p) => ({ ...p, page: 1 })); }}
              sx={{ minWidth: 250 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Rolle</InputLabel>
              <Select value={filters.role} onChange={(e) => { setFilters({ ...filters, role: e.target.value }); setPagination((p) => ({ ...p, page: 1 })); }} label="Rolle" size="small">
                <MenuItem value="">Alle Rollen</MenuItem>
                <MenuItem value="pending">⏳ Ausstehend (Dienstleister)</MenuItem>
                <MenuItem value="contractor">Dienstleister</MenuItem>
                <MenuItem value="customer">Kunde</MenuItem>
                <MenuItem value="staff">Mitarbeiter</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPagination((p) => ({ ...p, page: 1 })); }} label="Status" size="small">
                <MenuItem value="">Alle Status</MenuItem>
                <MenuItem value="active">Aktiv</MenuItem>
                <MenuItem value="inactive">Inaktiv</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {users.length === 0 ? (
          <Typography>Keine Nutzer gefunden.</Typography>
        ) : (
          <>
            <UserTable users={users} onActionClick={handleActionClick} onEditClick={handleEditClick} isLoading={isLoading} />
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={pagination.page} onChange={(_, p) => setPagination((prev) => ({ ...prev, page: p }))} color="primary" />
              </Box>
            )}
          </>
        )}

        {selectedUser && (
          <UserActionsModal open={openModal} onClose={() => { setOpenModal(false); setSelectedUser(null); }} user={selectedUser} action={modalAction} onSuccess={() => { setOpenModal(false); setSelectedUser(null); loadUsers(); }} />
        )}

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            Profil bearbeiten: {editUser?.name}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Grunddaten</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Anrede</InputLabel>
                  <Select value={editForm.anrede || ''} onChange={(e) => setEditForm({ ...editForm, anrede: e.target.value })} label="Anrede">
                    <MenuItem value="">–</MenuItem>
                    {ANREDE_OPTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth size="small" label="Name" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="E-Mail" type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Telefon" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Unternehmensname" value={editForm.company_name || ''} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gesellschaftsform</InputLabel>
                  <Select value={editForm.gesellschaftsform || ''} onChange={(e) => setEditForm({ ...editForm, gesellschaftsform: e.target.value })} label="Gesellschaftsform">
                    <MenuItem value="">–</MenuItem>
                    {GESELLSCHAFTSFORMEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Position im Unternehmen" value={editForm.position || ''} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>Adresse</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth size="small" label="Straße / Nr." value={editForm.strasse || ''} onChange={(e) => setEditForm({ ...editForm, strasse: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="PLZ" value={editForm.plz || ''} onChange={(e) => setEditForm({ ...editForm, plz: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Ort" value={editForm.ort || ''} onChange={(e) => setEditForm({ ...editForm, ort: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Land</InputLabel>
                  <Select value={editForm.land || 'Deutschland'} onChange={(e) => setEditForm({ ...editForm, land: e.target.value })} label="Land">
                    <MenuItem value="Deutschland">Deutschland</MenuItem>
                    <MenuItem value="Österreich">Österreich</MenuItem>
                    <MenuItem value="Schweiz">Schweiz</MenuItem>
                    <MenuItem value="EU-Ausland">EU-Ausland</MenuItem>
                    <MenuItem value="International">International</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>Weitere Kontaktdaten</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Mobil" value={editForm.mobil || ''} onChange={(e) => setEditForm({ ...editForm, mobil: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Fax" value={editForm.fax || ''} onChange={(e) => setEditForm({ ...editForm, fax: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Homepage" value={editForm.homepage || ''} onChange={(e) => setEditForm({ ...editForm, homepage: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="USt-ID (nur EU)" value={editForm.ust_id || ''} onChange={(e) => setEditForm({ ...editForm, ust_id: e.target.value })} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={isSaving}
              sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
            >
              {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Speichern'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PortalLayout>
  );
};

export default AdminUserManagementPage;
